/**
 * Utility to detect and pre-select the user's country
 * based on their browser's regional settings
 */

// Mapping of common timezones to country codes
const TIMEZONE_TO_COUNTRY = {
  'Europe/Brussels': 'BE',
  'Europe/Paris': 'FR',
  'Europe/Luxembourg': 'LU',
  'Europe/Amsterdam': 'NL',
  'Europe/Berlin': 'DE',
  'Europe/London': 'GB',
  'Europe/Madrid': 'ES',
  'Europe/Rome': 'IT',
  'Europe/Lisbon': 'PT',
  'Europe/Vienna': 'AT',
  'Europe/Zurich': 'CH',
  'America/New_York': 'US',
  'America/Los_Angeles': 'US',
  'America/Chicago': 'US',
  'America/Toronto': 'CA',
  'America/Montreal': 'CA',
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Australia/Sydney': 'AU',
  // Add more mappings as needed
};

/**
 * Detects the country code from the user's browser timezone
 * @returns {string} The 2-letter ISO country code. Defaults to "BE" if detection fails.
 */
export const detectBrowserCountryCode = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryCode = TIMEZONE_TO_COUNTRY[timeZone];

    if (countryCode) {
      return countryCode;
    }

    // Default to Belgium if timezone is not in the mapping
    return "BE";
  } catch (error) {
    console.warn("Unable to detect country from browser, defaulting to BE:", error);
    return "BE";
  }
};

/**
 * Finds a country in a list by its ISO code
 * @param {Array} countries - List of countries with structure {id, attributes: {code, name}}
 * @param {string} countryCode - 2-letter ISO country code
 * @returns {Object|null} The matching country or null if not found
 */
export const findCountryByCode = (countries, countryCode) => {
  if (!countryCode || !countries || countries.length === 0) {
    return null;
  }

  return countries.find(
    (country) => country.attributes?.code === countryCode
  );
};

/**
 * Vue composable for automatic country detection and pre-selection
 * @param {Object} options - Configuration options
 * @param {Ref} options.countries - Ref containing the list of countries
 * @param {Function} options.setFieldValue - Function to set a field value
 * @returns {Object} Methods to manage country detection
 */
export const useCountryDetection = ({ countries, setFieldValue }) => {
  /**
   * Pre-selects a country field based on the detected country code
   * @param {string} fieldPath - Path of the field to pre-select (e.g., "relationships.birthCountry.data.id")
   * @param {Ref} currentValue - Current value of the field (to check if it's empty)
   */
  const preselectCountryField = (fieldPath, currentValue) => {
    // Do nothing if the field already has a value
    if (currentValue?.value) {
      return;
    }

    const detectedCountryCode = detectBrowserCountryCode();

    if (!detectedCountryCode) {
      return;
    }

    const matchedCountry = findCountryByCode(countries.value, detectedCountryCode);

    if (matchedCountry) {
      setFieldValue(fieldPath, matchedCountry.id);
    }
  };

  /**
   * Pre-selects multiple country fields at once
   * @param {Array} fields - Array of objects {fieldPath, currentValue}
   */
  const preselectMultipleCountryFields = (fields) => {
    const detectedCountryCode = detectBrowserCountryCode();

    if (!detectedCountryCode) {
      return;
    }

    const matchedCountry = findCountryByCode(countries.value, detectedCountryCode);

    if (!matchedCountry) {
      console.warn('No matching country found for code:', detectedCountryCode);
      return;
    }

    fields.forEach(({ fieldPath, currentValue }) => {
      // Only pre-select if the field is empty
      if (!currentValue?.value) {
        setFieldValue(fieldPath, matchedCountry.id);
      }
    });
  };

  return {
    preselectCountryField,
    preselectMultipleCountryFields,
    detectBrowserCountryCode,
    findCountryByCode: (countryCode) => findCountryByCode(countries.value, countryCode)
  };
};
